import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    {
      name: "Services",
      path: "/services",
      dropdown: [
        { name: "All Services", path: "/services" },
        { name: "Aviation Operations", path: "/aviation" },
        { name: "Aircraft Detailing", path: "/aircraft-detailing" },
        { name: "Medical Delivery", path: "/medical-delivery" },
        { name: "Logistics", path: "/logistics" },
        { name: "Aerial Photography", path: "/aerial-photography" },
        { name: "Web Services", path: "/web-services" },
      ],
    },
    { name: "Aircraft Brokerage", path: "/aircraft-brokerage" },
    { name: "Mission Support", path: "/mission-support" },
    { name: "Gov Contracting", path: "/government-contracting" },
    { name: "Certifications", path: "/certifications" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="navbar">
      {/* Top bar */}
      <div className="bg-[#1a365d] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <span className="font-heading tracking-wider text-xs sm:text-sm">VETERAN-OWNED GOVERNMENT CONTRACTOR</span>
          <div className="hidden md:flex items-center gap-6">
            <a href="tel:504-319-7683" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <Phone className="w-4 h-4" />
              504-319-7683
            </a>
            <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <Mail className="w-4 h-4" />
              AMREID@AMRGLOBALHOLDINGS.COM
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Clean horizontal layout */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg"
              alt="AMR Global Holdings LLC - Veteran-Owned Aviation Infrastructure Company in New Orleans"
              className="h-14 w-14 object-contain flex-shrink-0"
            />
            <div className="ml-4 flex-shrink-0 hidden sm:flex sm:flex-col">
              <span className="font-heading text-lg text-[#1a365d] leading-none tracking-wide whitespace-nowrap">
                AMR GLOBAL HOLDINGS LLC
              </span>
              <span className="text-[10px] text-slate-500 tracking-[0.2em] mt-1 whitespace-nowrap">
                AVIATION • LOGISTICS • MISSION SUPPORT
              </span>
            </div>
            {/* Mobile-only abbreviated text */}
            <div className="ml-3 flex-shrink-0 sm:hidden">
              <span className="font-heading text-base text-[#1a365d] leading-none">AMR GLOBAL</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((link) =>
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`nav-link px-3 py-2 text-xs font-medium tracking-wide uppercase flex items-center gap-1 ${
                        isActive(link.path) ? "text-[#c5a04a]" : "text-[#1a365d] hover:text-[#c5a04a]"
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {link.dropdown.map((sublink) => (
                      <DropdownMenuItem key={sublink.name} asChild>
                        <Link
                          to={sublink.path}
                          className="w-full cursor-pointer"
                          data-testid={`nav-dropdown-${sublink.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {sublink.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`nav-link px-3 py-2 text-xs font-medium tracking-wide uppercase whitespace-nowrap ${
                    isActive(link.path) ? "text-[#c5a04a]" : "text-[#1a365d] hover:text-[#c5a04a]"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* Mobile/Tablet menu button */}
          <button
            className="xl:hidden p-2 text-[#1a365d] flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.path}
                  className={`block px-4 py-3 text-sm font-medium tracking-wide uppercase ${
                    isActive(link.path) ? "text-[#c5a04a] bg-slate-50" : "text-[#1a365d]"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
                {link.dropdown && (
                  <div className="pl-4 space-y-1">
                    {link.dropdown.slice(1).map((sublink) => (
                      <Link
                        key={sublink.name}
                        to={sublink.path}
                        className="block px-4 py-2 text-xs text-slate-600 uppercase tracking-wide"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 px-4">
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full btn-tactical btn-primary text-xs">
                  Request Capability Statement
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
